import {
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityIdUpdateProps,
  type EntityOnSaveProps,
} from "@firecms/core";
import {
  canAdminAccount,
  getAccountPath,
  type AccountCollectionId,
} from "../utils";
import type { AuthExtras } from "../types/auth";
import { NAV_GROUP_ACCESS_MANAGEMENT } from "./navigationGroups";
import type { AccountUserRole } from "./accountUsers";

export type InvitationStatus = "pending" | "accepted";

export type Invitation = {
  email: string;
  role: AccountUserRole;
  status: InvitationStatus;
  invitedBy: string;
  createdAt?: Date;
  acceptedAt?: Date;
  acceptedByUserId?: string;
};

export const normalizeInvitationEmail = (email: string): string =>
  email.trim().toLowerCase();

/** Provisional doc id while creating; FireCMS onIdUpdate cannot return "". */
export const NEW_INVITATION_ENTITY_ID = "__new__";

export const invitationEntityIdFromEmail = (email: string): string =>
  normalizeInvitationEmail(email) || NEW_INVITATION_ENTITY_ID;

const invitationStatusEnumValues = {
  pending: "Pending",
  accepted: "Accepted",
} as const;

export const buildInvitationsPath = (id: AccountCollectionId) =>
  `${getAccountPath(id)}/invitations`;

export const buildInvitationsCollection = (id: AccountCollectionId) =>
  buildCollection<Invitation>({
    name: "Invitations",
    singularName: "Invitation",
    description:
      "Invite someone by email to manage this account. TeamOps emails them a link to the admin. They must sign in with this address to accept.",
    id: "invitations",
    path: buildInvitationsPath(id),
    icon: "Mail",
    group: NAV_GROUP_ACCESS_MANAGEMENT,
    defaultSize: "xs",
    defaultViewMode: "table",
    hideIdFromForm: true,
    previewProperties: ["email", "role", "status"],
    propertiesOrder: ["email", "role", "status"],
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canManage = canAdminAccount(id, extra);
      return {
        read: canManage,
        edit: false,
        create: canManage,
        delete: canManage,
      };
    },
    callbacks: buildEntityCallbacks({
      onIdUpdate({ values }: EntityIdUpdateProps): string {
        const email =
          typeof values?.email === "string" ? values.email : "";
        return invitationEntityIdFromEmail(email);
      },
      onPreSave({
        values,
        status,
        context,
      }: EntityOnSaveProps<Invitation>) {
        const email = normalizeInvitationEmail(values.email ?? "");
        if (!email) {
          throw new Error("Email is required");
        }
        values.email = email;
        values.role = "manager";
        if (status === "new") {
          values.status = "pending";
          const uid = context.authController.user?.uid;
          if (!uid) {
            throw new Error("Authentication required");
          }
          values.invitedBy = uid;
          values.createdAt = new Date();
        }
        return values;
      },
    }),
    properties: {
      email: buildProperty({
        name: "Email",
        dataType: "string",
        validation: { required: true },
        description:
          "They must sign in with this address (Google or email/password).",
      }),
      role: buildProperty({
        name: "Role",
        dataType: "string",
        validation: { required: true },
        enumValues: {
          manager: "Manager",
        },
      }),
      status: buildProperty({
        name: "Status",
        dataType: "string",
        readOnly: true,
        enumValues: invitationStatusEnumValues,
      }),
      invitedBy: buildProperty({
        name: "Invited by",
        dataType: "string",
        readOnly: true,
        hideFromCollection: true,
      }),
      createdAt: buildProperty({
        name: "Created at",
        dataType: "date",
        readOnly: true,
        hideFromCollection: true,
      }),
      acceptedAt: buildProperty({
        name: "Accepted at",
        dataType: "date",
        readOnly: true,
        hideFromCollection: true,
      }),
      acceptedByUserId: buildProperty({
        name: "Accepted by",
        dataType: "string",
        readOnly: true,
        hideFromCollection: true,
      }),
    },
  });
